import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState } from "../ui";

export default function AdminAssociadosImportar() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/associados/importar/template", {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ablemp-modelo-importacao-associados.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao baixar o modelo.");
    }
  };

  const submit = async () => {
    if (!file) {
      toast.error("Selecione a planilha .xlsx");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/admin/associados/importar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      const { created_count, failed_count } = res.data;
      if (failed_count === 0) {
        toast.success(`${created_count} associado(s) importado(s) com sucesso!`);
      } else {
        toast.warning(
          `${created_count} criado(s), ${failed_count} com erro. Veja detalhes abaixo.`
        );
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-associados-import">
      <Link
        to="/admin/associados"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-[#002776]"
      >
        <ArrowLeft size={14} /> Voltar aos associados
      </Link>
      <PageHeader
        title="Importar Associados em Massa"
        subtitle="Envie uma planilha .xlsx para criar vários associados de uma vez. Baixe primeiro o modelo, preencha as linhas e faça o upload."
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-start gap-4 mb-6">
            <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#002776]/10 text-[#002776]">
              <FileSpreadsheet size={22} />
            </div>
            <div className="flex-1">
              <h3 className="font-serif-display text-xl">
                1. Baixe o modelo da planilha
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Cabeçalhos padronizados com todos os campos suportados +
                exemplo pré-preenchido.
              </p>
              <button
                data-testid="import-download-template"
                onClick={downloadTemplate}
                className="mt-3 inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
              >
                <Download size={14} /> Baixar modelo .xlsx
              </button>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#009B3A]/10 text-[#009B3A]">
                <Upload size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-serif-display text-xl">
                  2. Envie a planilha preenchida
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  O sistema cria os associados com status <b>ativo</b>. Se a
                  senha estiver vazia, geramos uma automática (aparece no
                  resultado).
                </p>

                <input
                  ref={inputRef}
                  data-testid="import-file"
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="mt-4 block text-sm"
                />

                {file && (
                  <div className="mt-2 text-xs text-slate-600">
                    Arquivo selecionado:{" "}
                    <span className="font-mono">{file.name}</span> (
                    {(file.size / 1024).toFixed(1)} KB)
                  </div>
                )}

                <button
                  data-testid="import-submit"
                  onClick={submit}
                  disabled={busy || !file}
                  className="mt-4 inline-flex items-center gap-2 bg-[#002776] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#0A0F14] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? "Enviando..." : "Importar planilha"}
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-serif-display text-xl mb-3">Dicas</h3>
          <ul className="text-sm text-slate-600 space-y-2 leading-relaxed">
            <li>
              <b>Colunas obrigatórias</b>: nome e e-mail principal.
            </li>
            <li>
              Se <b>person_type</b> estiver vazio, é definido automaticamente
              (CNPJ → PJ, CPF → PF).
            </li>
            <li>
              Se <b>tipo</b> estiver vazio, usa &quot;Contribuinte&quot;.
            </li>
            <li>
              Se <b>senha</b> estiver vazia, geramos uma automática — repasse
              ao associado depois.
            </li>
            <li>
              E-mail duplicado (já cadastrado) é reportado como erro e o
              associado não é criado.
            </li>
            <li>Até 10 MB por arquivo.</li>
          </ul>
        </Card>
      </div>

      {result && (
        <Card className="p-6 mt-6" testid="import-result">
          <div className="flex items-start gap-3 mb-4">
            <div
              className={`h-10 w-10 grid place-items-center rounded-sm ${
                result.failed_count === 0
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {result.failed_count === 0 ? (
                <CheckCircle2 size={18} />
              ) : (
                <AlertTriangle size={18} />
              )}
            </div>
            <div>
              <h3 className="font-serif-display text-xl">
                Resultado da importação
              </h3>
              <p className="text-sm text-slate-500">
                {result.total_read} linhas lidas ·{" "}
                <b className="text-emerald-700">
                  {result.created_count} criado(s)
                </b>{" "}
                ·{" "}
                <b className="text-rose-700">{result.failed_count} com erro</b>
              </p>
            </div>
          </div>

          {result.created?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 mb-2">
                Criados
              </h4>
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-emerald-50 text-[10px] uppercase tracking-wide text-emerald-800">
                    <tr>
                      <th className="p-2 text-left w-16">Linha</th>
                      <th className="p-2 text-left">Nome</th>
                      <th className="p-2 text-left">E-mail</th>
                      <th className="p-2 text-left">Senha gerada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.created.map((c) => (
                      <tr
                        key={c.email}
                        data-testid={`import-created-${c.email}`}
                        className="border-t border-slate-100"
                      >
                        <td className="p-2 text-slate-500">{c.row}</td>
                        <td className="p-2">{c.name}</td>
                        <td className="p-2">{c.email}</td>
                        <td className="p-2 font-mono text-xs">
                          {c.generated_password || (
                            <span className="text-slate-400">
                              (usou a informada)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {result.created.some((c) => c.generated_password) && (
                <p className="mt-2 text-[11px] text-slate-500">
                  ⚠️ Anote as senhas geradas — elas não serão exibidas novamente.
                </p>
              )}
            </div>
          )}

          {result.failed?.length > 0 && (
            <div className="mt-6">
              <h4 className="text-[11px] uppercase tracking-[0.22em] text-rose-700 mb-2">
                Erros
              </h4>
              <div className="overflow-x-auto border border-rose-200 rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-rose-50 text-[10px] uppercase tracking-wide text-rose-800">
                    <tr>
                      <th className="p-2 text-left w-16">Linha</th>
                      <th className="p-2 text-left">Nome</th>
                      <th className="p-2 text-left">E-mail</th>
                      <th className="p-2 text-left">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.failed.map((f, i) => (
                      <tr
                        key={`fail-${f.row ?? i}-${f.email || i}`}
                        className="border-t border-rose-100"
                      >
                        <td className="p-2 text-slate-500">{f.row}</td>
                        <td className="p-2">{f.name || "—"}</td>
                        <td className="p-2">{f.email || "—"}</td>
                        <td className="p-2 text-rose-700">{f.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      )}

      {!result && (
        <div className="mt-6">
          <EmptyState
            title="Nenhuma importação executada nesta sessão"
            hint="Após enviar a planilha, o resultado aparece aqui."
          />
        </div>
      )}
    </div>
  );
}
